import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, History, Users, Award, Heart } from "lucide-react";
import { Link } from "react-router-dom";

const AboutKente = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </Link>
            <h1 className="text-xl font-bold">About Kente</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Hero Section */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">The Story of Kente</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Kente is more than just a fabric—it's a symbol of African
                heritage, cultural identity, and artistic excellence that has
                been woven into the fabric of Ghanaian society for centuries.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* History Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Historical Origins
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            <p>
              Kente cloth originated in the Ashanti Kingdom of Ghana, with its
              roots tracing back to the 17th century. The name "Kente" comes
              from the Akan word "kenten," meaning "basket," reflecting the
              basket-like weaving pattern of the fabric.
            </p>
            <p>
              Traditionally, Kente was reserved for royalty and special
              occasions, symbolizing wealth, status, and cultural pride. Each
              pattern and color combination carries deep symbolic meaning, often
              representing proverbs, historical events, or philosophical
              concepts.
            </p>
            <p>
              Today, Kente has evolved from its royal origins to become a symbol
              of African identity worldwide, worn during graduations, weddings,
              and other significant life events.
            </p>
          </CardContent>
        </Card>

        {/* Cultural Significance */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5" />
              Cultural Significance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-3">Symbolic Meanings</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>
                    • <strong>Gold:</strong> Royalty, wealth, and spiritual
                    purity
                  </li>
                  <li>
                    • <strong>Red:</strong> Political and spiritual moods,
                    bloodshed
                  </li>
                  <li>
                    • <strong>Blue:</strong> Peacefulness, harmony, and love
                  </li>
                  <li>
                    • <strong>Green:</strong> Vegetation, planting, harvesting,
                    growth
                  </li>
                  <li>
                    • <strong>White:</strong> Purity, cleansing rites, and
                    festive occasions
                  </li>
                  <li>
                    • <strong>Black:</strong> Maturation, intensified spiritual
                    energy
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-3">Traditional Uses</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Royal ceremonies and state functions</li>
                  <li>• Religious and spiritual celebrations</li>
                  <li>• Weddings and naming ceremonies</li>
                  <li>• Academic achievements and graduations</li>
                  <li>• Cultural festivals and events</li>
                  <li>• Diplomatic and official occasions</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Weaving Process */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5" />
              Traditional Weaving Process
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold">Design Planning</h3>
                  <p className="text-sm text-muted-foreground">
                    Master weavers plan the pattern, selecting colors and motifs
                    that convey specific meanings and messages.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                  2
                </div>
                <div>
                  <h3 className="font-semibold">Thread Preparation</h3>
                  <p className="text-sm text-muted-foreground">
                    Silk or cotton threads are dyed using traditional methods
                    and prepared for the weaving process.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                  3
                </div>
                <div>
                  <h3 className="font-semibold">Hand Weaving</h3>
                  <p className="text-sm text-muted-foreground">
                    Skilled artisans use traditional looms to create intricate
                    patterns, a process that can take weeks or months for
                    complex designs.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                  4
                </div>
                <div>
                  <h3 className="font-semibold">Quality Inspection</h3>
                  <p className="text-sm text-muted-foreground">
                    Each piece is carefully inspected to ensure it meets the
                    highest standards of traditional craftsmanship.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Modern Kente */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Kente in the Modern World
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            <p>
              In contemporary times, Kente has transcended its traditional
              boundaries to become a global symbol of African heritage and
              pride. It's now commonly seen at:
            </p>
            <ul>
              <li>
                <strong>Academic Ceremonies:</strong> Graduation stoles and
                academic regalia
              </li>
              <li>
                <strong>Cultural Events:</strong> Festivals, concerts, and
                cultural celebrations
              </li>
              <li>
                <strong>Fashion:</strong> Modern clothing and accessories
              </li>
              <li>
                <strong>Art & Design:</strong> Home decor and artistic
                expressions
              </li>
              <li>
                <strong>Diplomatic Functions:</strong> Official state and
                international events
              </li>
            </ul>
            <p>
              Our platform honors this rich tradition while making authentic
              Kente designs accessible to people worldwide, allowing everyone to
              celebrate their heritage and create meaningful connections to
              African culture.
            </p>
          </CardContent>
        </Card>

        {/* Call to Action */}
        <Card>
          <CardContent className="pt-6 text-center">
            <h3 className="text-2xl font-bold mb-4">
              Create Your Own Kente Design
            </h3>
            <p className="text-muted-foreground mb-6">
              Honor the tradition of Kente by creating your own personalized
              design with authentic patterns and meaningful symbols.
            </p>
            <Link to="/designer">
              <Button size="lg">Start Designing</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AboutKente;
